import { AlertTriangle, ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { InvoiceAssigneeSelect } from "@/components/invoices/InvoiceAssigneeSelect";
import { ApprovalRequestRowActions } from "@/components/approval/ApprovalRequestRowActions";
import { InvoiceLabels } from "@/components/invoices/InvoiceLabels";
import { InvoiceRowActions } from "@/components/invoices/InvoiceRowActions";
import { CategoryMark, StatusCell } from "@/components/invoices/InvoiceQuickChange";
import { InvoiceStatusBadge } from "@/components/invoices/InvoiceStatusBadge";
import { useHasPermission } from "@/features/auth/useAuth";
import { PERMISSIONS } from "@/features/auth/permissions";
import { useContainerWidth } from "@/hooks/use-container-width";
import { getPanelTier } from "@/lib/responsive";
import { getFailureTypeColor, getFailureTypeLabel } from "@/lib/failedInvoiceTypes";
import {
  getVisibleColumns,
  SORTABLE_COLUMNS,
  TABLE_COLUMN_LABELS,
  TABLE_COLUMNS_BY_VARIANT,
} from "@/lib/invoicePageConfig";
import { formatCurrency, formatInvoiceDate, formatValueOrEmpty } from "@/lib/invoiceStatus";
import { cn } from "@/lib/utils";

const cellClass = "max-w-0 text-sm leading-tight text-foreground";

const isStatusColumn = (columnKey) =>
  columnKey === "reviewStatus" || columnKey === "processingStatus";

function EmptyInvoices({ variant }) {
  const isApproval = variant === "approval-requests";

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-0 flex-1 flex-col items-center justify-center bg-card px-6 py-16 text-center"
    >
      <p className="text-sm font-semibold text-foreground">
        {isApproval ? "No approval requests" : "No invoices in this view"}
      </p>
      <p className="mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
        {isApproval
          ? "You're all caught up, or try adjusting your search and filters."
          : "Change the queue filter or search to see matching invoices."}
      </p>
    </div>
  );
}

function InvoiceTableSkeleton({ columns }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-px p-4" role="status" aria-live="polite">
      <span className="sr-only">Loading invoices…</span>
      {Array.from({ length: 8 }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-3 py-2">
          {Array.from({ length: Math.min(columns, 8) }).map((__, cellIndex) => (
            <Skeleton key={cellIndex} className="h-4 flex-1 rounded-sm" />
          ))}
        </div>
      ))}
    </div>
  );
}

function FailureTypeBadge({ failureType }) {
  if (!failureType) return <span className="invoice-meta">—</span>;
  const color = getFailureTypeColor(failureType);
  return (
    <span
      className="failure-type-badge"
      style={{
        color,
        backgroundColor: `${color}1A`,
        borderColor: `${color}40`,
      }}
    >
      {getFailureTypeLabel(failureType)}
    </span>
  );
}

/**
 * Where clicking an invoice's title goes. The approval queue always opens the
 * editor and returns there; elsewhere the editor opens when the viewer may edit
 * this invoice (permission, the same rule the row's Edit action uses), and the
 * read-only view otherwise.
 */
function getDetailTarget(invoice, { linkMode, canEdit }) {
  if (linkMode === "edit") {
    return {
      path: `/invoice/edit/${invoice.id}`,
      state: { from: "approvalRequests", returnPath: "/approval-requests" },
    };
  }
  // TEMP: status lock removed, only the permission decides for now.
  // TODO: restore `canEdit && !getEditLockReason(invoice)` (import from
  // "@/lib/invoiceActions") and the status wording in the doc comment above.
  if (canEdit) {
    return { path: `/invoice/edit/${invoice.id}`, state: undefined };
  }
  return { path: `/invoice/view/${invoice.id}`, state: undefined };
}

function AssignedToCell({ invoice, assignees, onAssigneeChange, onRequestAssignees }) {
  return (
    <InvoiceAssigneeSelect
      invoice={invoice}
      assignees={assignees}
      onChange={onAssigneeChange}
      onRequestAssignees={onRequestAssignees}
    />
  );
}

function SupplierCell({ invoice, showDuplicateWarning, linkMode = "view", canEdit = false }) {
  const duplicate = invoice.duplicateOfInvoice;
  const { path: detailPath, state: linkState } = getDetailTarget(invoice, { linkMode, canEdit });

  return (
    <div className="flex min-w-0 items-center gap-2">
      <Link
        to={detailPath}
        state={linkState}
        onClick={(event) => event.stopPropagation()}
        className="truncate font-medium text-foreground hover:text-primary"
        title={invoice.companyName ?? undefined}
      >
        {formatValueOrEmpty(invoice.companyName)}
      </Link>

      {/* The warning is only useful if it leads somewhere: it links to the
          invoice this one duplicates, as the old app's icon did. */}
      {showDuplicateWarning && duplicate && (
        <Link
          to={`/invoice/view/${duplicate.guid}`}
          onClick={(event) => event.stopPropagation()}
          className="invoice-duplicate-chip invoice-duplicate-chip-link"
          title={`Duplicate of ${duplicate.invoiceNumber ?? "another invoice"} — open the original`}
        >
          <AlertTriangle className="size-3" aria-hidden="true" />
          Duplicate
        </Link>
      )}

      <InvoiceLabels labels={invoice.labels} maxVisible={1} className="shrink-0" />
    </div>
  );
}

function renderColumn(
  columnKey,
  invoice,
  {
    showDuplicateWarning,
    assignees,
    onAssigneeChange,
    onRequestAssignees,
    onDelete,
    deletingId,
    linkMode,
    onApprove,
    onReject,
    onChangeStatus,
    onChangeCategory,
    variant,
    compactActions,
    canEdit,
  },
) {
  const { path: detailPath, state: linkState } = getDetailTarget(invoice, { linkMode, canEdit });

  switch (columnKey) {
    case "type":
      return <CategoryMark invoice={invoice} onSelect={onChangeCategory} />;
    case "supplier":
      return (
        <SupplierCell
          invoice={invoice}
          showDuplicateWarning={showDuplicateWarning}
          linkMode={linkMode}
          canEdit={canEdit}
        />
      );
    case "invoiceNumber":
      return (
        <Link
          to={detailPath}
          state={linkState}
          onClick={(event) => event.stopPropagation()}
          className="invoice-id truncate hover:text-primary"
          title={invoice.invoiceNumber ?? undefined}
        >
          {formatValueOrEmpty(invoice.invoiceNumber)}
        </Link>
      );
    case "invoiceDate":
      return <span className="invoice-meta">{formatInvoiceDate(invoice.invoiceDate)}</span>;
    case "orderNumber":
      return (
        <span className="invoice-meta truncate" title={invoice.orderNumber ?? undefined}>
          {formatValueOrEmpty(invoice.orderNumber)}
        </span>
      );
    case "netAmount":
      return (
        <span className="invoice-amount">
          {formatCurrency(invoice.netAmount, invoice.currencySymbol)}
        </span>
      );
    case "carriage":
      return (
        <span className="invoice-amount">
          {formatCurrency(invoice.carriage, invoice.currencySymbol)}
        </span>
      );
    case "totalAmount":
      return (
        <span className="invoice-amount">
          {formatCurrency(invoice.totalAmount, invoice.currencySymbol)}
        </span>
      );
    case "reviewStatus":
      return <InvoiceStatusBadge invoice={invoice} kind="review" compact />;
    case "processingStatus":
      return <StatusCell invoice={invoice} kind="processing" onSelect={onChangeStatus} />;
    case "failureType":
      return <FailureTypeBadge failureType={invoice.failureType} />;
    case "assignedTo":
      return (
        <AssignedToCell
          invoice={invoice}
          assignees={assignees}
          onAssigneeChange={onAssigneeChange}
          onRequestAssignees={onRequestAssignees}
        />
      );
    case "options":
      if (variant === "approval-requests") {
        return (
          <ApprovalRequestRowActions
            invoice={invoice}
            onApprove={onApprove}
            onReject={onReject}
          />
        );
      }
      return (
        <InvoiceRowActions
          invoice={invoice}
          onDelete={onDelete}
          isDeleting={deletingId === invoice.id}
          compact={compactActions}
        />
      );
    default:
      return null;
  }
}

/**
 * The record fields a card shows under the invoice number, chosen from the
 * columns the queue actually configures rather than hardcoded.
 *
 * The previous card showed the same four fields on every queue, which meant the
 * Failed queue's cards omitted the failure type — the single field those
 * operators triage on — while the desktop table gave it a column.
 */
function InvoiceCardMeta({ invoice, columns, variant }) {
  const has = (key) => columns.includes(key);

  return (
    <div className="invoice-card-meta">
      {has("reviewStatus") && <InvoiceStatusBadge invoice={invoice} kind="review" compact />}
      {has("processingStatus") && (
        <InvoiceStatusBadge invoice={invoice} kind="processing" compact />
      )}
      {has("failureType") && invoice.failureType && (
        <FailureTypeBadge failureType={invoice.failureType} />
      )}
      {has("invoiceDate") && invoice.invoiceDate && (
        <span className="invoice-meta">{formatInvoiceDate(invoice.invoiceDate)}</span>
      )}
      {has("assignedTo") &&
        (invoice.assignedTo ?? (variant === "approval-requests" ? "Unassigned" : null)) && (
          <span className="invoice-meta truncate">
            {invoice.assignedTo ?? "Unassigned"}
          </span>
        )}
    </div>
  );
}

/**
 * One invoice as a record card.
 *
 * Three separate targets, deliberately: the checkbox selects, the body opens the
 * invoice, the menu acts on it. The previous card made the entire body a
 * `<button>` whose only job was toggling selection — so the primary task of the
 * app, opening an invoice, was reachable only through a 28px eye icon, and that
 * icon was a `<Link>` nested inside the `<button>`, which is invalid HTML with
 * no defined touch behaviour.
 */
function InvoiceCard({
  invoice,
  isSelected,
  onToggleRow,
  columns,
  variant,
  linkMode,
  canEdit,
  onApprove,
  onReject,
  onDelete,
  deletingId,
}) {
  const { path: detailPath, state: linkState } = getDetailTarget(invoice, { linkMode, canEdit });

  return (
    <li className={cn("invoice-card", isSelected && "is-selected")}>
      <Checkbox
        checked={isSelected}
        onCheckedChange={() => onToggleRow(invoice.id)}
        aria-label={`Select invoice ${invoice.invoiceNumber}`}
        className="invoice-card-select"
      />

      <Link to={detailPath} state={linkState} className="invoice-card-body">
        <div className="invoice-card-line">
          <span className="invoice-card-id">
            <CategoryMark invoice={invoice} />
            <span className="invoice-id truncate" title={invoice.invoiceNumber ?? undefined}>
              {formatValueOrEmpty(invoice.invoiceNumber)}
            </span>
          </span>
          <span className="invoice-amount invoice-card-amount">
            {formatCurrency(invoice.totalAmount, invoice.currencySymbol)}
          </span>
        </div>

        <p className="invoice-card-supplier" title={invoice.companyName ?? undefined}>
          {formatValueOrEmpty(invoice.companyName)}
        </p>

        <InvoiceCardMeta invoice={invoice} columns={columns} variant={variant} />
      </Link>

      <div className="invoice-card-actions">
        {variant === "approval-requests" ? (
          <ApprovalRequestRowActions
            invoice={invoice}
            onApprove={onApprove}
            onReject={onReject}
          />
        ) : (
          <InvoiceRowActions
            invoice={invoice}
            onDelete={onDelete}
            isDeleting={deletingId === invoice.id}
            compact
          />
        )}
      </div>
    </li>
  );
}

function InvoiceCardList({
  invoices,
  selectedIds,
  onToggleRow,
  onToggleAll,
  allSelected,
  partiallySelected,
  columns,
  variant,
  linkMode,
  canEdit,
  onApprove,
  onReject,
  onDelete,
  deletingId,
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <label className="invoice-card-selectall">
        <Checkbox
          checked={allSelected}
          indeterminate={partiallySelected}
          onCheckedChange={onToggleAll}
          aria-label={
            partiallySelected
              ? "Select remaining invoices on this page"
              : "Select all invoices on this page"
          }
        />
        <span>Select page</span>
      </label>

      <ul className="min-h-0 flex-1 overflow-y-auto">
        {invoices.map((invoice) => (
          <InvoiceCard
            key={invoice.id}
            invoice={invoice}
            isSelected={selectedIds.has(invoice.id)}
            onToggleRow={onToggleRow}
            columns={columns}
            variant={variant}
            linkMode={linkMode}
            canEdit={canEdit}
            onApprove={onApprove}
            onReject={onReject}
            onDelete={onDelete}
            deletingId={deletingId}
          />
        ))}
      </ul>
    </div>
  );
}

/**
 * A sortable column header. Sorting is server-side, so the header only reports
 * the intent; `sortBy`/`sortDirection` come back down as the current state.
 */
function SortableHead({ columnKey, label, sortBy, sortDirection, onSortChange }) {
  const field = SORTABLE_COLUMNS[columnKey];
  if (!field || !onSortChange) return label;

  const isActive = sortBy === field;
  const nextDirection = isActive && sortDirection === "asc" ? "desc" : "asc";
  const Icon = !isActive ? ChevronsUpDown : sortDirection === "asc" ? ArrowUp : ArrowDown;

  return (
    <button
      type="button"
      className="table-sort-trigger"
      aria-label={`Sort by ${label}, ${nextDirection === "asc" ? "ascending" : "descending"}`}
      onClick={() => onSortChange(field, nextDirection)}
    >
      {label}
      <Icon
        className={cn("size-3 shrink-0", isActive ? "text-foreground" : "text-muted-foreground/60")}
        aria-hidden="true"
      />
    </button>
  );
}

export function InvoiceDataTable({
  variant,
  invoices,
  selectedIds,
  onToggleRow,
  onToggleAll,
  assignees = [],
  onAssigneeChange,
  onRequestAssignees,
  onApprove,
  onReject,
  onDelete,
  onChangeStatus,
  onChangeCategory,
  sortBy = null,
  sortDirection = null,
  onSortChange,
  deletingId = null,
  isLoading = false,
  caption = "Invoices",
}) {
  const allColumns =
    TABLE_COLUMNS_BY_VARIANT[variant] ?? TABLE_COLUMNS_BY_VARIANT["invoice-manager"];
  const showDuplicateWarning = variant !== "failed-invoices" && variant !== "inQuery-invoices";
  const linkMode = variant === "approval-requests" ? "edit" : "view";
  const canEdit = useHasPermission()(PERMISSIONS.UPDATE_INVOICE);

  // The density question is about this box, not the window. A tablet in portrait
  // and a desktop with the rail open can be the same width here, and both should
  // render the same table — which asking `window.innerWidth` could never do,
  // because the rail's 15rem is invisible to it.
  const [measureRef, width] = useContainerWidth();
  const tier = getPanelTier(width);
  const columns = getVisibleColumns(variant, tier);

  const allSelected = invoices.length > 0 && invoices.every((invoice) => selectedIds.has(invoice.id));
  const someSelected = invoices.some((invoice) => selectedIds.has(invoice.id));
  const partiallySelected = someSelected && !allSelected;

  // Row actions collapse to one labelled menu everywhere but the full table:
  // four icon buttons need 140px, which is more than the options column gets
  // once columns start dropping.
  const compactActions = tier !== "full";

  const body = () => {
    if (isLoading) {
      // `columns` is empty at the cards tier by construction, and an unmeasured
      // panel has no tier at all — either way the skeleton needs a shape of its
      // own rather than the one column that arithmetic would give it.
      return <InvoiceTableSkeleton columns={Math.max(columns.length + 1, 5)} />;
    }

    if (invoices.length === 0) {
      return <EmptyInvoices variant={variant} />;
    }

    // `null` means the container has not reported yet. Holding for one frame
    // beats guessing and snapping between two very different layouts.
    if (tier == null) {
      return <InvoiceTableSkeleton columns={5} />;
    }

    if (tier === "cards") {
      return (
        <InvoiceCardList
          invoices={invoices}
          selectedIds={selectedIds}
          onToggleRow={onToggleRow}
          onToggleAll={onToggleAll}
          allSelected={allSelected}
          partiallySelected={partiallySelected}
          columns={allColumns}
          variant={variant}
          linkMode={linkMode}
          canEdit={canEdit}
          onApprove={onApprove}
          onReject={onReject}
          onDelete={onDelete}
          deletingId={deletingId}
        />
      );
    }

    return renderTable();
  };

  const renderTable = () => (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="table-scroll-hint min-h-0 flex-1 overflow-x-auto overscroll-x-contain">
        <Table className="invoice-table-min invoice-table-dense">
          <caption className="sr-only">{caption}</caption>
          <TableHeader className="table-head-surface sticky top-0 z-10 border-b border-line">
            <TableRow className="table-head-surface border-line hover:bg-transparent">
              <TableHead className="table-head-cell w-10">
                <Checkbox
                  checked={allSelected}
                  indeterminate={partiallySelected}
                  onCheckedChange={onToggleAll}
                  aria-label={
                    partiallySelected
                      ? "Select remaining invoices on this page"
                      : "Select all invoices on this page"
                  }
                />
              </TableHead>
              {columns.map((columnKey) => (
                <TableHead
                  key={columnKey}
                  className={cn(
                    "table-head-cell",
                    columnKey === "type" && "w-10 px-2",
                    (columnKey === "netAmount" || columnKey === "carriage" || columnKey === "totalAmount") && "text-end",
                    columnKey === "options" && (compactActions ? "w-14 text-end" : "w-28 text-end"),
                    columnKey === "assignedTo" && "text-center",
                    columnKey === "failureType" && "text-center",
                    isStatusColumn(columnKey) && "text-center",
                  )}
                >
                  {TABLE_COLUMN_LABELS[columnKey] ? (
                    <SortableHead
                      columnKey={columnKey}
                      label={TABLE_COLUMN_LABELS[columnKey]}
                      sortBy={sortBy}
                      sortDirection={sortDirection}
                      onSortChange={onSortChange}
                    />
                  ) : (
                    <span className="sr-only">Category</span>
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((invoice) => {
              const isSelected = selectedIds.has(invoice.id);
              return (
                <TableRow
                  key={invoice.id}
                  aria-selected={isSelected}
                  data-state={isSelected ? "selected" : undefined}
                  className={cn(
                    "motion-row group/row cursor-pointer border-b border-line-subtle bg-card hover:bg-muted/70",
                    isSelected && "bg-muted hover:bg-muted",
                  )}
                  onClick={() => onToggleRow(invoice.id)}
                >
                  <TableCell
                    className={cn(cellClass, "w-10 max-w-none")}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => onToggleRow(invoice.id)}
                      aria-label={`Select invoice ${invoice.invoiceNumber}`}
                    />
                  </TableCell>
                  {columns.map((columnKey) => (
                    <TableCell
                      key={columnKey}
                      className={cn(
                        cellClass,
                        columnKey === "type" && "w-10 max-w-none px-2",
                        columnKey === "supplier" && "max-w-56",
                        columnKey === "invoiceNumber" && "max-w-40",
                        (columnKey === "netAmount" || columnKey === "carriage" || columnKey === "totalAmount") && "max-w-none text-end",
                        isStatusColumn(columnKey) && "max-w-36 text-center",
                        columnKey === "failureType" && "max-w-40 text-center",
                        columnKey === "assignedTo" && "max-w-36 text-center",
                        columnKey === "options" && "max-w-none text-end",
                      )}
                      onClick={
                        columnKey === "options" ||
                        columnKey === "assignedTo" ||
                        isStatusColumn(columnKey) ||
                        columnKey === "type"
                          ? (event) => event.stopPropagation()
                          : undefined
                      }
                    >
                      {renderColumn(columnKey, invoice, {
                        showDuplicateWarning,
                        assignees,
                        onAssigneeChange,
                        onRequestAssignees,
                        onDelete,
                        deletingId,
                        linkMode,
                        canEdit,
                        onApprove,
                        onReject,
                        onChangeStatus,
                        onChangeCategory,
                        variant,
                        compactActions,
                      })}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  // The measured element is the outer wrapper, so it reports the same width for
  // every branch — the tier cannot oscillate because a card list and a table
  // happen to have different intrinsic widths.
  return (
    <div ref={measureRef} className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {body()}
    </div>
  );
}
